import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { CityPropType, NewBookingDeliveryPropType } from 'common/prop-types';
import { Popover } from 'grab-ui';
import cx from 'classnames';
import circleSvg from 'public/icons/bulkupload/circle.svg';
import circleClickedSvg from 'public/icons/bulkupload/circle-clicked.svg';
import _get from 'lodash/get';
import _every from 'lodash/every';
import _sumBy from 'lodash/sumBy';
import calcPrice from 'common/utils/calc-price';
import NewBookingDuck from 'ducks/new-booking-duck';
import { paymentMethodSelector } from 'selectors/booking-selectors';
import {
  isCashlessPaymentDisabledSelector,
  isCashPaymentDisabledSelector,
  selectedCitySelector,
} from 'selectors/app-selector';
import { deliveriesFromPropsSelector } from 'selectors/new-booking-selectors';
import {
  CashPaymentMethods,
  generatePaymentMethodToRender,
} from 'common/anytime-booking/payment-method';
import { showFailedPaymentsRetryModalAction } from 'actions/app-actions';
import { getAllPaxPaymentMethodsV2 } from 'actions/booking-actions';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { PayAndBookContext } from 'views/v2/new-bulk-upload/common/context';
import styles from './payment-method-selector.less';

const PaymentMethodButton = ({
  paymentMethod,
  selected,
  onPaymentMethodSelect,
}) => {
  const {
    icon,
    displayName,
    disabled,
    disabledReason,
    ctaText,
    ctaFunc,
  } = paymentMethod;

  const button = (
    <a
      className={cx(styles.paymentMethod, {
        [styles.disabled]: disabled,
        [styles.selected]: selected,
      })}
      onClick={onPaymentMethodSelect(paymentMethod)}
    >
      <img src={icon} alt="" className={styles.icon} />
      <div className={styles.name}>{displayName}</div>
      <img
        src={selected ? circleClickedSvg : circleSvg}
        alt=""
        className={styles.checkBox}
      />
    </a>
  );

  if (disabled && (disabledReason || ctaText || ctaFunc)) {
    return (
      <Popover
        content={
          <div>
            {disabledReason && (
              <p className={styles.disabledReason}> {disabledReason} </p>
            )}
            {ctaText &&
              ctaFunc && (
                <a className={styles.disabledCTA} onClick={ctaFunc}>
                  {ctaText}
                </a>
              )}
          </div>
        }
        trigger={'hover'}
        zIndex={10000}
        placement={'bottom'}
        overlayClassName={styles.disabledPopover}
      >
        {button}
      </Popover>
    );
  }

  return button;
};

PaymentMethodButton.propTypes = {
  paymentMethod: PropTypes.shape({}).isRequired,
  selected: PropTypes.bool.isRequired,
  onPaymentMethodSelect: PropTypes.func.isRequired,
};

const PaymentMethodSelector = ({
  dispatch,
  deliveryIDs,
  deliveries,
  paymentMethods,
  city,
  isCashPaymentDisabled,
  isCashlessPaymentDisabled,
}) => {
  const { totalFinalFare } = useContext(PayAndBookContext);

  const onRetryFailedPayments = useCallback(
    () => {
      dispatch(showFailedPaymentsRetryModalAction());
    },
    [dispatch],
  );

  const onReloadPaymentMethods = useCallback(
    () => {
      dispatch(getAllPaxPaymentMethodsV2(_get(city, 'countryCode')));
    },
    [dispatch, city],
  );

  const accountPaymentMethods = useMemo(
    () => [...paymentMethods, ...CashPaymentMethods],
    [paymentMethods],
  );

  const paymentMethodsToRender = useMemo(
    () => {
      if (deliveries.length === 0) {
        return [];
      }

      const currency = _get(deliveries[0], 'quote.currency');
      const totalFinalFare = _sumBy(deliveries, delivery =>
        _get(delivery, 'quote.finalFare.lowerBound', 0),
      );

      const paymentMethodOptions = {
        totalAmount: calcPrice(totalFinalFare, currency),
        totalDeliveries: deliveries.length,
        isCashlessPaymentDisabled,
        isCashPaymentDisabled,
        onRetryFailedPayments,
        onReloadPaymentMethods,
      };

      const paymentMethodsToRender = accountPaymentMethods.map(paymentMethod =>
        generatePaymentMethodToRender(
          paymentMethod,
          deliveries[0],
          city,
          paymentMethodOptions,
        ),
      );

      deliveries.slice(1).forEach(delivery => {
        accountPaymentMethods.forEach((paymentMethod, index) => {
          const paymentMethodOverwrite = generatePaymentMethodToRender(
            paymentMethod,
            delivery,
            city,
            paymentMethodOptions,
          );
          if (
            !paymentMethodsToRender[index].disabled &&
            paymentMethodOverwrite.disabled
          ) {
            paymentMethodsToRender[index].disabled =
              paymentMethodOverwrite.disabled;
            paymentMethodsToRender[index].disabledReason =
              paymentMethodOverwrite.disabledReason;
            paymentMethodsToRender[index].ctaText =
              paymentMethodOverwrite.ctaText;
            paymentMethodsToRender[index].ctaFunc =
              paymentMethodOverwrite.ctaFunc;
          }
        });
      });

      return paymentMethodsToRender;
    },
    [
      city,
      deliveries,
      accountPaymentMethods,
      isCashlessPaymentDisabled,
      isCashPaymentDisabled,
      onRetryFailedPayments,
      onReloadPaymentMethods,
    ],
  );

  const selectedPaymentTypeID = useMemo(
    () => {
      const allPaymentTypeIDs = deliveries.map(delivery =>
        _get(delivery, 'detail.paymentTypeID'),
      );
      if (
        _every(
          allPaymentTypeIDs,
          paymentTypeID => paymentTypeID === allPaymentTypeIDs[0],
        )
      ) {
        return allPaymentTypeIDs[0];
      }
      return undefined;
    },
    [deliveries],
  );

  const onPaymentMethodSelect = useCallback(
    paymentMethod => () => {
      const { disabled, paymentTypeID } = paymentMethod;
      if (disabled) {
        return;
      }
      dispatch(
        NewBookingDuck.creators.bulkUpdateDeliveryDetail(deliveryIDs, {
          paymentTypeID,
        }),
      );
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressBukUploadBookPaymentMethod(
          paymentTypeID,
          totalFinalFare,
        ),
      );
    },
    [dispatch, deliveryIDs, totalFinalFare],
  );

  useEffect(
    () => {
      if (
        !selectedPaymentTypeID &&
        _every(deliveries, delivery => !delivery.fetchingQuote)
      ) {
        const autoSelectPaymentMethod =
          paymentMethodsToRender.find(
            paymentMethod => paymentMethod.isPrimary,
          ) ||
          paymentMethodsToRender.find(paymentMethod => !paymentMethod.disabled);
        if (autoSelectPaymentMethod) {
          onPaymentMethodSelect(autoSelectPaymentMethod)();
        }
      }
    },
    [
      deliveries,
      selectedPaymentTypeID,
      onPaymentMethodSelect,
      paymentMethodsToRender,
    ],
  );

  return (
    <div className={styles.paymentMethodSelector}>
      {paymentMethodsToRender.map(paymentMethod => (
        <PaymentMethodButton
          key={paymentMethod.paymentTypeID}
          paymentMethod={paymentMethod}
          selected={selectedPaymentTypeID === paymentMethod.paymentTypeID}
          onPaymentMethodSelect={onPaymentMethodSelect}
        />
      ))}
    </div>
  );
};

PaymentMethodSelector.propTypes = {
  dispatch: PropTypes.func.isRequired,
  deliveryIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  deliveries: PropTypes.arrayOf(NewBookingDeliveryPropType).isRequired,
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  city: CityPropType.isRequired,
  isCashPaymentDisabled: PropTypes.bool.isRequired,
  isCashlessPaymentDisabled: PropTypes.bool.isRequired,
};

export default connect((state, props) => ({
  deliveries: deliveriesFromPropsSelector(state, props),
  paymentMethods: paymentMethodSelector(state),
  city: selectedCitySelector(state),
  isCashPaymentDisabled: isCashPaymentDisabledSelector(state),
  isCashlessPaymentDisabled: isCashlessPaymentDisabledSelector(state),
}))(PaymentMethodSelector);
